<template>
  <VBlock class="product" :direction="Direction.column" :sort="Sort.center_center">
    <VBlock class="product-name-wrapper" :sort="Sort.space_between">
      <slot name="title"></slot>
      <VA class="delete-text-wrapper" :onClick="onDelete">
        <VSpan class="delete-text" :color="TextColor.brand" text="삭제" :size="16" />
      </VA>
    </VBlock>
    <VA class="bottom-wrapper" :onClick="onMoveDetail">
      <VBlock :sort="Sort.space_between">
        <VCard class="image-wrapper" :radius="3" :color="ColorType.gray">
          <div class="blank-space"></div>
          <VImage
            class="image-background"
            :type="ImageType.img"
            :src="resources.MarketThumbnail"
            :name="`${name}${index}_background`"
            :width="117"
            :height="117"
            :radius="3"
          />
          <VImage
            v-if="image !== '' && image"
            class="image-hover"
            :type="ImageType.img"
            :src="itemImage"
            :name="`${name}${index}`"
            :width="117"
            :height="117"
            :radius="3"
          />
          <div v-if="isUnpurchasable" class="image-edge-case-wrapper">
            <VSpan :text="imageEdgeCaseText" :size="18" :color="TextColor.white" />
          </div>
        </VCard>
        <VBlock class="detail" :direction="Direction.column" :sort="Sort.left_top">
          <VBlock class="info-wrapper" :direction="Direction.column" :sort="Sort.left_center">
            <slot name="name-list"></slot>
            <VSpan
              class="price"
              :text="`(${salePrice}원)`"
              :size="16"
              :color="!isUnpurchasable ? TextColor.gray : TextColor.disable"
              :type="TextType.Number"
            />
          </VBlock>
          <VBlock class="price-wrapper" :sort="Sort.left_center">
            <strike :class="['origin-price', isUnpurchasable && 'inactive-price']" v-if="isSale">
              <VSpan
                :text="`${totalRetailPrice}원`"
                :size="14"
                :color="!isUnpurchasable ? TextColor.gray : TextColor.inactive"
                :type="TextType.Number"
              />
            </strike>
            <VSpan
              :text="`${totalSalePrice}원`"
              :size="18"
              :weight="TextWeight.bold"
              :color="!isUnpurchasable ? TextColor.darkGray : TextColor.inactive"
              :type="TextType.Number"
            />
          </VBlock>
          <div class="message-wrapper" v-if="!returnOrExchange">
            <VP
              v-if="firstPurchaseOnlyProduct && isPurchasable"
              class="message"
              text="첫 주문 특가 상품! 1인 1개만 구매할 수 있어요"
              :size="12"
              :color="TextColor.warning"
            />
            <VP
              v-else-if="notFirstPurchase"
              class="message"
              text="B마트 첫 주문 시 구매 가능한 상품입니다"
              :size="12"
              :color="TextColor.emergency"
            />
            <VP
              v-else-if="notFirstPurchaseGuest"
              class="message"
              text="B마트 회원 전용 첫 주문 특가 상품입니다"
              :size="12"
              :color="TextColor.emergency"
            />
            <VP
              v-else-if="(stockCount - quantity < 0) && isPurchasable"
              class="message"
              :text="`재고가 ${stockCount}개 :( 수량을 조절해주세요.`"
              :size="12"
              :color="TextColor.emergency"
            />
            <VP
              v-else-if="(maxCount - quantity <= 0) && isPurchasable"
              class="message"
              :text="`해당 상품은 한번에 ${maxCount || 100}개까지 구매할 수 있어요.`"
              :size="12"
              :color="TextColor.emergency"
              :type="TextType.Number"
            />
            <VP
              v-else-if="(minCount - quantity > 0) && isPurchasable"
              class="message"
              :text="`해당 상품은 한번에 ${minCount}개 이상부터 구매할 수 있어요.`"
              :size="12"
              :color="TextColor.emergency"
              :type="TextType.Number"
            />
            <VP
              v-else-if="(stockCount - quantity <= 2) && isPurchasable"
              class="message"
              :text="`품절 임박! 얼른 구매해주세요.`"
              :size="12"
              :color="TextColor.warning"
              :type="TextType.Number"
            />
          </div>
          <slot v-if="isPurchasable"></slot>
        </VBlock>
      </VBlock>
    </VA>
  </VBlock>
</template>

<script>
import VImage, { ImageType } from '@/presentationals/v2/components/layouts/atoms/VImage'
import VP, {
  TextWeight,
  TextColor,
  TextType
} from '@/presentationals/v2/components/layouts/atoms/VP'
import VSpan from '@/presentationals/v2/components/layouts/atoms/VSpan'

import VA from '@/presentationals/v2/components/layouts/molecules/VA'
import VBlock, { Direction, Sort } from '@/presentationals/v2/components/layouts/molecules/VBlock'
import VCard, { ColorType } from '@/presentationals/v2/components/layouts/molecules/VCard'

import MarketThumbnail from '@/assets/images/market-thumbnail.png'

export default {
  name: 'MarketItem',
  props: {
    index: {
      type: Number,
      required: true
    },
    returnOrExchange: {
      type: Boolean,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    checked: {
      type: Boolean,
      required: true
    },
    disabled: {
      type: Boolean,
      required: true
    },
    image: {
      type: String,
      required: true
    },
    soldOut: {
      type: Boolean,
      required: true
    },
    isPurchasable: {
      type: Boolean,
      required: true
    },
    isUnpurchasable: {
      type: Boolean,
      required: true
    },
    firstPurchaseOnlyProduct: {
      type: Boolean,
      required: true
    },
    notFirstPurchase: {
      type: Boolean,
      required: true
    },
    notFirstPurchaseGuest: {
      type: Boolean,
      required: true
    },
    stockCount: {
      type: Number,
      required: true
    },
    salePrice: {
      type: Number,
      required: true
    },
    retailPrice: {
      type: Number,
      required: true
    },
    quantity: {
      type: Number,
      required: true
    },
    minCount: {
      type: Number,
      required: true
    },
    maxCount: {
      type: Number,
      required: true
    },
    onChecked: {
      type: Function,
      required: true
    },
    onDelete: {
      type: Function,
      required: true
    },
    onMoveDetail: {
      type: Function,
      required: true
    }
  },
  components: {
    VImage,
    VP,
    VSpan,

    VA,
    VBlock,
    VCard
  },
  computed: {
    isSale() {
      return this.salePrice !== this.retailPrice
    },
    totalRetailPrice() {
      return this.retailPrice * this.quantity
    },
    totalSalePrice() {
      return this.salePrice * this.quantity
    },
    itemImage() {
      return this.image
    },
    imageEdgeCaseText() {
      let text = '상품 준비중'
      if (this.soldOut) text = '다 팔렸어요'
      if (this.notFirstPurchase || this.notFirstPurchaseGuest) text = '구매불가'

      return text
    }
  },
  data() {
    return {
      Direction,
      Sort,
      ImageType,
      TextColor,
      ColorType,
      TextWeight,
      TextType,

      resources: {
        MarketThumbnail
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/modules/colors';

.product {
  width: 100%;
  height: auto;

  .product-name-wrapper {
    width: 100%;
    box-sizing: border-box;
    align-items: flex-start;
    margin-bottom: 15px;
    // margin-top: 12px;

    .delete-text-wrapper {
      height: 28px;
      box-sizing: border-box;
      padding-top: 3px;

      .delete-text {
        height: 100%;
      }
    }
  }

  .bottom-wrapper {
    width: 100%;
    height: auto;

    .image-wrapper {
      position: relative;
      width: 117px;
      height: 117px;
      margin-right: 16px;
      border: 1px solid $light-gray-8;

      .blank-space {
        width: 117px;
        height: 117px;
      }

      .image-background {
        position: absolute;
        left: 0;
        top: 0;
        z-index: 1;
        width: 100%;
        height: 100%;
        border-radius: 3px;
      }

      .image-hover {
        position: absolute;
        left: 0;
        top: 0;
        z-index: 2;
        width: 100%;
        height: 100%;
        border-radius: 3px;
        background-color: $light-gray-8;
      }

      .image-edge-case-wrapper {
        top: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        z-index: 3;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        border-radius: 3px;
      }
    }
    .detail {
      width: calc(100% - 135px);
      overflow: hidden;
      text-overflow: ellipsis;

      .info-wrapper {
        width: 100%;
        height: auto;
        margin-bottom: 5px;

        .price {
          height: 19px;
        }
      }

      .price-wrapper {
        height: 19px;
        margin-bottom: 4px;

        .origin-price {
          color: $gray-2;
          margin-right: 4px;
        }

        .inactive-price {
          color: $light-gray-4;
        }
      }

      .message-wrapper {
        display: flex;
        height: 32px;
        justify-content: flex-start;
        align-items: center;
        margin-bottom: 2px;
        vertical-align: text-bottom;

        .message {
          height: auto;
          line-height: 1.3;
          white-space: pre-line;
          text-align: left;
          letter-spacing: -0.3px;
        }
      }
    }
  }
}
</style>